import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface Matiere {
  id: number;
  code_matiere: string;
  nom_matiere: string;
  credits?: number;
  coefficient?: number;
  ue?: number | null;
  ue_nom?: string | null;
  ue_code?: string | null;
  niveau?: string;
  semestre?: number;
  option?: string | null;
  classe?: string | null;
  coefficient_devoir?: number;
  coefficient_examen?: number;
  type_matiere?: string;
  actif?: boolean;
}

export interface BatchCreateMatiereParams {
  noms: string;
  niveau: string;
  semestre: number;
  option_ids?: number[];
}

export interface BatchCreateMatiereResult {
  success: boolean;
  message: string;
  matieres: Matiere[];
}

export interface CreerTPParams {
  nom_tp: string;
  credits: number;
  coefficient_devoir: number;
  coefficient_examen: number;
  niveau: string;
  semestre: number;
  option_ids: number[];
}

export interface CreerTPResult {
  success: boolean;
  message: string;
  details: Array<{
    option_id: number;
    option_nom: string;
    ue_id: number;
    ue_code: string;
    matiere_id: number;
    matiere_code: string;
  }>;
}

export interface MatiereEnseignant {
  enseignant: number | null;
  enseignant_nom: string | null;
  enseignant_nom_seul?: string | null;
  enseignant_prenoms?: string | null;
}

export interface MatiereFilters {
  niveau?: string;
  option?: string;
  classe?: string;  // Code de classe (ex "L1AI") — filtre strict via Option.code
  semestre?: number;
  // Non filtrant côté backend (schéma choisi via header X-Annee-Academique),
  // mais inclus dans la clé de cache RTK pour refetch les bonnes matières quand
  // on bascule d'année (les matiere_id diffèrent d'un schéma à l'autre).
  annee_academique?: string;
}

export const matieresApi = createApi({
  reducerPath: 'matieresApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Matieres'],
  endpoints: (builder) => ({
    getMatieres: builder.query<Matiere[], Record<string, any> | void>({
      query: (params) => ({
        url: '/matieres/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        // Gérer la réponse paginée ou directe
        if (response && response.results) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Matieres'],
    }),

    getMatieresByFilters: builder.query<Matiere[], MatiereFilters>({
      query: (filters) => ({
        url: '/matieres/',
        params: filters,
      }),
      transformResponse: (response: any) => {
        if (response && response.results) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Matieres'],
    }),

    getMatiereById: builder.query<Matiere, number>({
      query: (id) => `/matieres/${id}/`,
      providesTags: ['Matieres'],
    }),

    getMatiereEnseignant: builder.query<MatiereEnseignant, { matiereId: number; anneeAcademique?: string }>({
      query: ({ matiereId, anneeAcademique }) => ({
        url: `/matieres/${matiereId}/enseignant/`,
        params: anneeAcademique ? { annee_academique: anneeAcademique } : undefined,
      }),
    }),

    createMatiere: builder.mutation<Matiere, Partial<Matiere>>({
      query: (matiere) => ({
        url: '/matieres/',
        method: 'POST',
        body: matiere,
      }),
      invalidatesTags: ['Matieres'],
    }),

    updateMatiere: builder.mutation<Matiere, { id: number; matiere: Partial<Matiere> }>({
      query: ({ id, matiere }) => ({
        url: `/matieres/${id}/`,
        method: 'PUT',
        body: matiere,
      }),
      invalidatesTags: ['Matieres'],
    }),

    deleteMatiere: builder.mutation<void, number>({
      query: (id) => ({
        url: `/matieres/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Matieres'],
    }),

    batchCreateMatieres: builder.mutation<BatchCreateMatiereResult, BatchCreateMatiereParams>({
      query: (data) => ({
        url: '/matieres/batch_create/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Matieres'],
    }),

    creerTPMultiOptions: builder.mutation<CreerTPResult, CreerTPParams>({
      query: (data) => ({
        url: '/matieres/creer_tp_multi_options/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Matieres'],
    }),

    toggleActifMatiere: builder.mutation<{ success: boolean; id: number; actif: boolean; message: string }, { id: number; actif?: boolean }>({
      query: ({ id, actif }) => ({
        url: `/matieres/${id}/toggle_actif/`,
        method: 'POST',
        body: actif !== undefined ? { actif } : {},
      }),
      invalidatesTags: ['Matieres'],
    }),

    toggleActifMultiple: builder.mutation<{ success: boolean; nb_modifiees: number; actif: boolean; message: string }, { ids: number[]; actif: boolean }>({
      query: (data) => ({
        url: '/matieres/toggle_actif_multiple/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Matieres'],
    }),

    copierMatieresVersOption: builder.mutation<
      { success: boolean; message: string; created_matieres: number; created_ues: number; skipped: number },
      { matiere_ids: number[]; options_cibles: string[] }
    >({
      query: (data) => ({
        url: '/matieres/copier_vers_option/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Matieres'],
    }),

    getOrphelines: builder.query<{ count: number; matieres: Matiere[] }, void>({
      query: () => '/matieres/orphelines/',
      providesTags: ['Matieres'],
    }),

    rattacherMatieres: builder.mutation<
      { success: boolean; rattachees: number; message: string },
      { matiere_ids: number[]; option_nom: string }
    >({
      query: (data) => ({
        url: '/matieres/rattacher/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Matieres'],
    }),

    nettoyerOrphelines: builder.mutation<
      {
        success?: boolean;
        total: number;
        nb_supprimables?: number;
        nb_avec_dependances?: number;
        supprimables?: Array<{ id: number; code: string; nom: string; niveau: string; semestre: number }>;
        avec_dependances?: Array<{ id: number; code: string; nom: string; niveau: string; semestre: number; dependances: Record<string, number> }>;
        nb_supprimes?: number;
        nb_desactives?: number;
        message?: string;
      },
      { mode: 'analyser' | 'executer' }
    >({
      query: (data) => ({
        url: '/matieres/nettoyer_orphelines/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Matieres'],
    }),

    getSujetsAImprimer: builder.query<
      {
        niveau: string;
        option: string | null;
        semestre: string | null;
        nb_etudiants_total: number;
        nb_matieres: number;
        matieres: Array<{
          matiere_id: number;
          code_matiere: string;
          nom_matiere: string;
          ue_code: string | null;
          option: string | null;
          niveau: string;
          semestre: number;
          nb_sujets: number;
          options_concernees: string[];
        }>;
      },
      { niveau: string; option?: string; semestre?: string }
    >({
      query: (params) => ({
        url: '/matieres/sujets_a_imprimer/',
        params,
      }),
    }),
  }),
});

export const {
  useGetMatieresQuery,
  useGetMatieresByFiltersQuery,
  useGetMatiereByIdQuery,
  useGetMatiereEnseignantQuery,
  useCreateMatiereMutation,
  useUpdateMatiereMutation,
  useDeleteMatiereMutation,
  useBatchCreateMatieresMutation,
  useCreerTPMultiOptionsMutation,
  useToggleActifMatiereMutation,
  useToggleActifMultipleMutation,
  useCopierMatieresVersOptionMutation,
  useGetOrphelinesQuery,
  useRattacherMatieresMutation,
  useNettoyerOrphelinesMutation,
  useGetSujetsAImprimerQuery,
} = matieresApi;
